/* global awc */

import Swiper from 'swiper/bundle'; // eslint-disable-line import/no-unresolved
import debounce from '../helpers/debounce';

const breakpointSM = 600;
const breakpointLG = 992;

const defaultBlockConfig = {
	alwaysEnabled: false,
	initialSlide: 0,
	edgeless: false,
	loop: false,
	a11yLabels: [],
};

/**
 * Class to handle the initialisation and control over Swiper sliders.
 * It handles all sliders on the page at once.
 *
 * Config has two tiers, in this order:
 *   1. Author-tunable  — instanceConfig (global) then blockConfig (per-instance)
 *   2. Framework-owned — always computed from the DOM, always wins
 *
 * @property {Array}          swipers    - Array of { swiperDOM, blockConfig } entries.
 * @property {MediaQueryList} breakpoint - The breakpoint to check if the slider should be initialised or disabled.
 */
export default class Slider {
	swipers = [];
	breakpoint = window.matchMedia( `(width < ${ breakpointLG }px)` );

	//#region Initialisation

	/**
	 * Constructor to initialise the class.
	 *
	 * @param {Object}  options                   - The options to initialise the Swiper.
	 * @param {string}  options.selector          - The selector to get all the Swiper elements that should be initialised.
	 * @param {string}  options.slideClass        - The class name of the slides in the Swiper.
	 * @param {boolean} options.reinitOnTabChange - Whether to reinitialise swipers on tab navigation events.
	 * @param {Object}  options.instanceConfig    - Extra configuration options for the Swiper.
	 */
	constructor( {
		selector = '.swiper',
		slideClass = 'swiper-slide',
		reinitOnTabChange = true,
		instanceConfig = {},
	} ) {
		this.selector = `${ selector }:not(.swiper-initialized):has(.swiper-wrapper)`;
		this.slideClass = slideClass;
		this.reinitOnTabChange = reinitOnTabChange;
		this.instanceConfig = instanceConfig;

		this.destroySwipersRef = this.destroySwipers.bind( this );
		this.initialiseSwipersRef = this.initialiseSwipers.bind( this );

		this.breakpoint.addEventListener( 'change', this.syncSwipersToBreakpoint.bind( this ) );

		this.initialiseSwipers();
		this.observeTabNavigation();
	}

	/**
	 * Function to initialise all the Swiper sliders on the page.
	 */
	initialiseSwipers() {
		document.querySelectorAll( this.selector )?.forEach( ( swiperDOM ) => {
			let parsedConfig = {};
			try {
				parsedConfig = JSON.parse( swiperDOM?.dataset?.swiperConfig ?? '{}' );
			} catch {
				// malformed config — proceed with defaults
			}

			const blockConfig = { ...defaultBlockConfig, ...parsedConfig };

			if ( ! ( this.breakpoint.matches || blockConfig.alwaysEnabled ) ) {
				return;
			}

			const entry = { swiperDOM, blockConfig };
			this.createSwiper( entry );
			this.swipers.push( entry );
		} );
	}

	/**
	 * Create a Swiper instance for an entry and wire up loop re-evaluation.
	 * Swiper attaches itself to `swiperDOM.swiper` on construction — that's
	 * the instance's only home; we don't keep a second reference of our own.
	 *
	 * Our setup uses `slidesPerView: 'auto'` and `spaceBetween: 0`
	 * because with Gutenberg we must adjust to fluid slide and gap width.
	 * So main "slides per view" calculations are delegated to css.
	 *
	 * @param {Object}      entry
	 * @param {number|null} initialSlide - Slide to start on when rebuilding an
	 *                                   already-live slider (e.g. after a loop
	 *                                   reinit), so position is preserved.
	 */
	createSwiper( entry, initialSlide = null ) {
		const { swiperDOM, blockConfig } = entry;
		// Associate the swiper container with its section heading via aria-labelledby,
		// generating a unique ID on the heading if it doesn't already have one.
		const blockWrapper = swiperDOM.closest( '.wp-block-awc-base, section, .wp-block-group' );
		if ( blockWrapper ) {
			const heading = blockWrapper.querySelector( 'h1, h2, h3' );
			if ( heading && heading.textContent.trim() ) {
				if ( ! heading.id ) {
					heading.id = `swiper-heading-${ crypto.randomUUID().slice( 0, 8 ) }`;
				}
				swiperDOM.setAttribute( 'aria-labelledby', heading.id );
			}
		}

		const config = {
			...this.instanceConfig,

			a11y: {
				containerRole: 'group',
				...blockConfig.a11yLabels,
			},
			keyboard: { enabled: true },
			grabCursor: true,
			slidesPerView: 'auto',
			watchSlidesProgress: true,
			slideClass: this.slideClass,
			centeredSlides: blockConfig.centeredSlides ?? false,
			initialSlide: initialSlide ?? blockConfig.initialSlide,
			loop: this.shouldLoop( swiperDOM, blockConfig ),
			pagination: this.getPaginationConfig( swiperDOM ),
			navigation: this.getNavigationConfig( swiperDOM ),
			breakpoints: this.getColumnBreakpoints( swiperDOM, blockConfig ),
			on: this.getEvents( entry ),
		};

		new Swiper( swiperDOM, config );
	}

	/**
	 * Breakpoints derived from `blockConfig.columns` (single source of truth —
	 * `getEffectiveSlidesPerGroup` reads the same object). Edgeless sliders
	 * skip columns entirely. When looping, an uneven tier falls back to
	 * slidesPerGroup: 1 so the wrap never shows a partial group.
	 *
	 * Reads `blockConfig.loop` (the raw opt-in), not `shouldLoop()`'s result —
	 * `shouldLoop` depends on this method via `getEffectiveSlidesPerGroup`, so
	 * resolving loop state here first would be circular.
	 *
	 * @param {HTMLElement} swiperDOM
	 * @param {Object}      blockConfig
	 * @return {Object|undefined} Breakpoints config, or undefined if none apply.
	 */
	getColumnBreakpoints( swiperDOM, blockConfig ) {
		if ( blockConfig.edgeless || ! blockConfig.columns || blockConfig.columns <= 1 ) {
			return undefined;
		}

		const slideCount = this.getSlideCount( swiperDOM );
		const tiers = [
			{ width: breakpointSM, slidesPerGroup: 2 },
			{ width: breakpointLG, slidesPerGroup: blockConfig.columns },
		];

		const breakpoints = {};
		tiers.forEach( ( { width, slidesPerGroup } ) => {
			const dividesEvenly = slideCount % slidesPerGroup === 0;
			breakpoints[ width ] = {
				slidesPerGroup: blockConfig.loop && ! dividesEvenly ? 1 : slidesPerGroup,
			};
		} );

		return breakpoints;
	}

	/**
	 * @param {HTMLElement} swiperDOM
	 * @return {Object|boolean} Pagination config, or false if no pagination element exists.
	 */
	getPaginationConfig( swiperDOM ) {
		const paginationEl = swiperDOM.querySelector( '.swiper-pagination' );
		return paginationEl ? { el: paginationEl, type: 'bullets' } : false;
	}

	/**
	 * @param {HTMLElement} swiperDOM
	 * @return {Object} Navigation config.
	 */
	getNavigationConfig( swiperDOM ) {
		return {
			addIcons: false,
			nextEl: swiperDOM.querySelector( '.swiper-button-next' ) || null,
			prevEl: swiperDOM.querySelector( '.swiper-button-prev' ) || null,
		};
	}

	/**
	 * Build the `on` event handlers for an entry. All instances keep slide
	 * tabindex in sync with visibility: init sets the correct state on first
	 * render; resize corrects it after layout changes affect how many slides
	 * are visible. Loop-enabled instances additionally get debounced loop
	 * re-evaluation.
	 *
	 * @param {Object} entry
	 * @return {Object} Event handlers keyed by Swiper event name.
	 */
	getEvents( entry ) {
		const events = {
			// Force focus rules immediately on load
			init: ( swiper ) => this.updateSlideTabindex( swiper ),

			resize: ( swiper ) => this.updateSlideTabindex( swiper ),

			// covers keyboard/button navigation
			slideChangeTransitionEnd: ( swiper ) => this.updateSlideTabindex( swiper ),

			// covers touch navigation (fired once when drag ends)
			touchEnd: ( swiper ) => this.updateSlideTabindex( swiper ),

			// only comes in when `observer: true` (ex. with query).
			observerUpdate: ( swiper ) => {
				// reset to first slide, no transition, no callbacks
				swiper.slideTo( 0, 0, false );

				// reinitialise pagination.
				swiper.pagination.destroy();
				swiper.pagination.init();
				swiper.pagination.render();
				swiper.pagination.update();
			},
		};

		if ( entry.blockConfig.loop ) {
			const reevaluateLoop = debounce( () => this.reevaluateLoop( entry ), 150 );

			events.observerUpdate = reevaluateLoop;
			events.resize = ( swiper ) => {
				reevaluateLoop();
				this.updateSlideTabindex( swiper );
			};
		}

		return events;
	}

	//#endregion

	//#region Loop capability

	/**
	 * Whether Swiper's `loop` should be on. `blockConfig.loop` is the
	 * author's opt-in; even then, we only loop once there are enough real
	 * slides for Swiper's own buffer (v11+ floor: slidesPerView +
	 * slidesPerGroup — we add one slide of headroom since
	 * slidesPerView: 'auto' tends to need it in practice).
	 *
	 * Always reads the live DOM, never a Swiper instance: this runs both
	 * before the first instance exists and right after the previous one was
	 * destroyed, so there's no instance that's reliably safe to ask.
	 *
	 * @param {HTMLElement} swiperDOM
	 * @param {Object}      blockConfig
	 * @return {boolean} Whether Swiper's `loop` option should be true.
	 */
	shouldLoop( swiperDOM, blockConfig ) {
		if ( ! blockConfig.loop ) {
			return false;
		}

		const slideCount = this.getSlideCount( swiperDOM );
		const visibleSlides = Math.ceil( this.estimateVisibleSlides( swiperDOM ) );
		const slidesPerGroup = this.getEffectiveSlidesPerGroup( swiperDOM, blockConfig );

		return slideCount >= visibleSlides + slidesPerGroup + 1;
	}

	/**
	 * Re-check the loop decision and reinit only if it actually flipped — not
	 * on every layout tick. Only ever called through the debounced wrappers
	 * set up in getEvents(), never directly.
	 *
	 * @param {Object} entry
	 */
	reevaluateLoop( entry ) {
		const { swiperDOM, blockConfig } = entry;
		const isLooping = swiperDOM.swiper.params.loop;

		if ( this.shouldLoop( swiperDOM, blockConfig ) === isLooping ) {
			return;
		}

		const position = isLooping ? swiperDOM.swiper.realIndex : swiperDOM.swiper.activeIndex;
		swiperDOM.swiper.destroy( true, false );

		this.createSwiper( entry, position );
	}

	/**
	 * Resolves getColumnBreakpoints() down to the single slidesPerGroup value
	 * that applies at the current viewport width.
	 *
	 * @param {HTMLElement} swiperDOM
	 * @param {Object}      blockConfig
	 * @return {number} Effective slidesPerGroup for the current viewport.
	 */
	getEffectiveSlidesPerGroup( swiperDOM, blockConfig ) {
		const breakpoints = this.getColumnBreakpoints( swiperDOM, blockConfig );
		if ( ! breakpoints ) {
			return 1;
		}

		if ( window.innerWidth >= breakpointLG ) {
			return breakpoints[ breakpointLG ]?.slidesPerGroup ?? 1;
		}

		return window.innerWidth >= breakpointSM
			? breakpoints[ breakpointSM ]?.slidesPerGroup ?? 1
			: 1;
	}

	/**
	 * Estimate visible slide count by measuring a slide's full inline
	 * footprint (width + margin — `offsetWidth` excludes margin, and
	 * Swiper's `spaceBetween` is implemented as margin) against the
	 * container width. slidesPerView is always 'auto', so this can't be
	 * shortcut with a known number.
	 *
	 * @param {HTMLElement} swiperDOM
	 * @return {number} Estimated visible slide count.
	 */
	estimateVisibleSlides( swiperDOM ) {
		const firstSlide = swiperDOM.querySelector( `.swiper-wrapper > .${ this.slideClass }` );
		if ( ! firstSlide ) {
			return 1;
		}

		const slideFootprint = firstSlide.offsetWidth + this.getSlideInlineMargin( firstSlide );

		return slideFootprint ? swiperDOM.offsetWidth / slideFootprint : 1;
	}

	//#endregion

	//#region Lifecycle control

	/**
	 * Keep `inert` in sync with visibility so only fully-visible slides stay
	 * keyboard-reachable. Slides Swiper marks "visible" but not "fully
	 * visible" (partially clipped — e.g. the last slide, where a trailing
	 * margin skews Swiper's own math) get a manual check against Swiper's
	 * translate/grid values, adjusted for the slide's own inline margin.
	 *
	 * @param {import('swiper').Swiper} swiper
	 */
	updateSlideTabindex( swiper ) {
		requestAnimationFrame( () => {
			swiper.slides.forEach( ( slideEl, i ) => {
				if ( slideEl.classList.contains( 'swiper-slide-fully-visible' ) ) {
					slideEl.removeAttribute( 'inert' );
					return;
				}

				if ( ! slideEl.classList.contains( 'swiper-slide-visible' ) ) {
					slideEl.setAttribute( 'inert', '' );
					return;
				}

				const slideBefore = swiper.translate + ( slideEl.swiperSlideOffset ?? 0 );
				const slideSize =
					swiper.slidesSizesGrid[ i ] - this.getSlideInlineMargin( slideEl );

				const isFullyVisible =
					slideBefore >= 0 && slideBefore <= swiper.size - slideSize + 1;

				slideEl.toggleAttribute( 'inert', ! isFullyVisible );
			} );
		} );
	}

	/**
	 * Function to enable all the Swiper sliders on the page.
	 */
	enableSwipers() {
		this.swipers.forEach( ( { swiperDOM } ) => swiperDOM.swiper.enable() );
	}

	/**
	 * Function to disable all the Swiper sliders on the page.
	 */
	disableSwipers() {
		this.swipers.forEach(
			( { swiperDOM, blockConfig } ) =>
				! blockConfig.alwaysEnabled && swiperDOM.swiper.disable()
		);
	}

	/**
	 * Function to destroy all the Swiper sliders on the page.
	 */
	destroySwipers() {
		this.swipers.forEach( ( { swiperDOM } ) => {
			swiperDOM.swiper.disable();
			swiperDOM.swiper.destroy( true, false );
		} );
		this.swipers = [];
	}

	/**
	 * Sync swiper enabled/disabled state to whether the breakpoint matches.
	 *
	 * @param {MediaQueryListEvent} e - The breakpoint change event.
	 */
	syncSwipersToBreakpoint( e ) {
		if ( e.matches ) {
			this.enableSwipers();
			this.initialiseSwipers();
		} else {
			this.disableSwipers();
		}

		this.observeTabNavigation();
	}

	/**
	 * Manage listening to the navigation event to reinitialise the Swipers.
	 */
	observeTabNavigation() {
		const hasAlwaysEnabled = this.swipers.some(
			( { blockConfig } ) => blockConfig.alwaysEnabled
		);
		const shouldObserve =
			this.reinitOnTabChange &&
			( this.breakpoint.matches || hasAlwaysEnabled ) &&
			( !! awc.isTabbablePage || this.swipers.length );

		if ( shouldObserve ) {
			document.addEventListener( 'awc-before-tab-update', this.destroySwipersRef );
			document.addEventListener( 'awc-after-tab-update', this.initialiseSwipersRef );
		} else {
			document.removeEventListener( 'awc-before-tab-update', this.destroySwipersRef );
			document.removeEventListener( 'awc-after-tab-update', this.initialiseSwipersRef );
		}
	}

	//#endregion

	//#region Helpers

	/**
	 * @param {HTMLElement} swiperDOM
	 * @return {number} Number of slides currently in the DOM.
	 */
	getSlideCount( swiperDOM ) {
		return swiperDOM.querySelectorAll( `.swiper-wrapper > .${ this.slideClass }` ).length;
	}

	/**
	 * `offsetWidth` (used to size/measure slides elsewhere) excludes margin,
	 * and `spaceBetween` is implemented as inline margin on each slide — so
	 * anything measuring a slide's true footprint needs this added back in.
	 *
	 * @param {HTMLElement} slideEl
	 * @return {number} Combined inline-start + inline-end margin, in pixels.
	 */
	getSlideInlineMargin( slideEl ) {
		const { marginInlineStart, marginInlineEnd } = getComputedStyle( slideEl );
		const inlineMargin = parseFloat( marginInlineStart ) + parseFloat( marginInlineEnd );

		return Number.isNaN( inlineMargin ) ? 0 : inlineMargin;
	}

	//#endregion Helpers
}

window.awc.Swiper = Slider;
